<nb-card class="client-quota-dialog">
  <nb-card-header>
    <div>
      <h4>{{ 'pages.admin.clientCredits.title' | translate }}</h4>
      <strong>{{ data?.company_name || data?.email }}</strong>
    </div>
    <button nbButton ghost status="basic" size="small" type="button" (click)="close()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </nb-card-header>

  <nb-card-body>
    <app-page-loader *ngIf="creditsLoading && !credits"></app-page-loader>

    <ng-container *ngIf="credits as c">
      <div class="meta-row">
        <span>{{ 'pages.admin.clientCredits.plan' | translate }}: <strong>{{ c.plan?.name || ('pages.admin.clientCredits.noPlan' | translate) }}</strong></span>
        <span>Client ID: {{ c.client_id }}</span>
        <span *ngIf="c.period_start">{{ 'pages.admin.clientCredits.periodSince' | translate: { date: formatDay(c.period_start) } }}</span>
      </div>

      <div class="stats">
        <div class="stat">
          <span>{{ 'pages.admin.clientCredits.planMonthly' | translate }}</span>
          <strong>{{ formatCredits(c.monthly_included_credits ?? -1) }}</strong>
        </div>
        <div class="stat">
          <span>{{ 'pages.admin.clientCredits.used' | translate }}</span>
          <strong>{{ formatCredits(c.used_credits) }}</strong>
        </div>
        <div class="stat">
          <span>{{ 'pages.admin.clientCredits.planRemaining' | translate }}</span>
          <strong>{{ formatCredits(c.plan_remaining_credits ?? -1) }}</strong>
        </div>
        <div class="stat">
          <span>{{ 'pages.admin.clientCredits.bought' | translate }}</span>
          <strong>{{ formatCredits(c.bought_remaining_credits) }}</strong>
        </div>
        <div class="stat">
          <span>{{ 'pages.admin.clientCredits.gifted' | translate }}</span>
          <strong>{{ formatCredits(c.gifted_remaining_credits) }}</strong>
        </div>
        <div class="stat stat--total">
          <span>{{ 'pages.admin.clientCredits.available' | translate }}</span>
          <strong>{{ formatCredits(c.available_credits) }}</strong>
        </div>
      </div>

      <section class="tile-flat gift">
        <header class="tile-flat__header">
          <h5>{{ 'pages.admin.clientCredits.giftTitle' | translate }}</h5>
        </header>
        <p class="hint">{{ 'pages.admin.clientCredits.giftHint' | translate }}</p>

        <form [formGroup]="giftForm" (ngSubmit)="requestGift()">
          <div class="gift-grid">
            <div class="field-row">
              <label class="label" for="gift_credits">{{ 'pages.admin.clientCredits.giftAmount' | translate }}</label>
              <input
                id="gift_credits"
                nbInput
                fullWidth
                type="number"
                min="1"
                [max]="c.max_gift_credits"
                formControlName="credits"
                data-enter-ignore
                [readonly]="confirmingGift"
              />
            </div>
            <div class="field-row">
              <label class="label" for="gift_note">{{ 'pages.admin.clientCredits.giftNote' | translate }}</label>
              <input
                id="gift_note"
                nbInput
                fullWidth
                formControlName="note"
                [placeholder]="'pages.admin.clientCredits.giftNotePlaceholder' | translate"
                data-enter-ignore
                [readonly]="confirmingGift"
              />
            </div>
          </div>

          <div class="field-row">
            <label class="label" for="gift_message">{{ 'pages.admin.clientCredits.giftMessage' | translate }}</label>
            <textarea
              id="gift_message"
              nbInput
              fullWidth
              rows="2"
              formControlName="customer_message"
              [placeholder]="'pages.admin.clientCredits.giftMessagePlaceholder' | translate"
              [readonly]="confirmingGift"
            ></textarea>
          </div>

          <nb-checkbox formControlName="notify_customer" [disabled]="confirmingGift">
            {{ 'pages.admin.clientCredits.giftNotify' | translate: { email: data?.email } }}
          </nb-checkbox>

          <div class="gift-confirm" *ngIf="confirmingGift">
            {{ 'pages.admin.clientCredits.giftConfirm' | translate: { credits: formatCredits(giftForm.value.credits), name: data?.company_name || data?.email } }}
          </div>

          <div class="actions">
            <ng-container *ngIf="!confirmingGift">
              <button nbButton type="submit" status="primary">
                {{ 'pages.admin.clientCredits.giftSubmit' | translate }}
              </button>
            </ng-container>
            <ng-container *ngIf="confirmingGift">
              <button nbButton type="button" status="basic" (click)="cancelGift()" [disabled]="granting">
                {{ 'buttons.cancel' | translate }}
              </button>
              <button nbButton type="button" status="primary" (click)="confirmGift()" [nbSpinner]="granting" [disabled]="granting">
                {{ 'pages.admin.clientCredits.giftConfirmButton' | translate }}
              </button>
            </ng-container>
          </div>
        </form>
      </section>

      <section class="tile-flat packages">
        <header class="tile-flat__header">
          <h5>{{ 'pages.admin.clientCredits.packagesTitle' | translate }}</h5>
        </header>
        <div class="audit-empty" *ngIf="c.packages.length === 0">
          {{ 'pages.admin.clientCredits.packagesEmpty' | translate }}
        </div>
        <div class="audit-list" *ngIf="c.packages.length > 0">
          <div class="audit-row" *ngFor="let p of c.packages; trackBy: trackPackage" [class.inactive]="!p.active">
            <div>
              <strong>{{ sourceLabel(p) }}</strong>
              <span>{{ formatCredits(p.credits_granted) }} Credits</span>
              <small>
                {{ 'pages.admin.clientCredits.remaining' | translate: { credits: formatCredits(p.credits_remaining) } }}
                · {{ 'pages.admin.clientCredits.expires' | translate: { date: formatDay(p.expires_at) } }}
                <ng-container *ngIf="p.eur_amount > 0"> · {{ p.eur_amount }} € netto</ng-container>
              </small>
            </div>
            <div>
              <span *ngIf="p.granted_by_email">{{ p.granted_by_email }}</span>
              <small>{{ formatDate(p.purchased_at) }}</small>
            </div>
            <p *ngIf="p.grant_note">{{ p.grant_note }}</p>
            <p *ngIf="p.customer_message" class="customer-message">„{{ p.customer_message }}“</p>
          </div>
        </div>
      </section>
    </ng-container>

    <details class="al-disclosure token-limit">
      <summary>{{ 'pages.admin.clientCredits.tokenSection' | translate }}</summary>
    <app-page-loader *ngIf="loading && !details"></app-page-loader>
    <ng-container *ngIf="details as quota">
      <div class="meta-row">
        <span>Plan: <strong>{{ quota.plan.name || 'Kein aktiver Plan' }}</strong></span>
        <span>Client ID: {{ quota.client_id }}</span>
      </div>

      <div class="empty" *ngIf="quota.quota_rows.length === 0">
        Für diesen Client wurde kein aktiver Quota-Datensatz gefunden.
      </div>

      <form *ngIf="quota.quota_rows.length > 0" [formGroup]="form" (ngSubmit)="save()">
        <div class="field-row" *ngIf="quota.quota_rows.length > 1">
          <label>Plan Endpoint</label>
          <nb-select
            fullWidth
            formControlName="api_usage_id"
            (selectedChange)="onRowChange($event)"
          >
            <nb-option
              *ngFor="let row of quota.quota_rows"
              [value]="row.api_usage_id"
            >
              {{ row.endpoint_name || row.endpoint_url || row.plan_endpoint_id }}
            </nb-option>
          </nb-select>
        </div>

        <div class="stats" *ngIf="selectedRow as row">
          <div class="stat">
            <span>Plan Basislimit</span>
            <strong>{{ formatNumber(row.plan_token_limit) }}</strong>
          </div>
          <div class="stat">
            <span>Aktives Client Limit</span>
            <strong>{{ formatNumber(row.active_token_quota_limit) }}</strong>
          </div>
          <div class="stat">
            <span>Verbraucht</span>
            <strong>{{ formatNumber(row.token_usage_count) }}</strong>
          </div>
          <div class="stat">
            <span>Remaining aktuell</span>
            <strong>{{ formatNumber(row.remaining_token_quota) }}</strong>
          </div>
          <div class="stat">
            <span>Remaining nach Änderung</span>
            <strong>{{ formatNumber(previewRemaining()) }}</strong>
          </div>
          <div class="stat">
            <span>Enforcement</span>
            <strong>{{ row.enforce_token_quota ? 'aktiv' : 'inaktiv' }}</strong>
          </div>
        </div>

        <div class="edit-grid">
          <div class="field-row">
            <label for="token_quota_limit">Neues Token Limit</label>
            <input
              id="token_quota_limit"
              nbInput
              fullWidth
              type="number"
              formControlName="token_quota_limit"
              placeholder="-1 für unlimited"
              [status]="form.get('token_quota_limit')?.invalid && form.get('token_quota_limit')?.touched ? 'danger' : 'basic'"
            />
          </div>

          <div class="field-row">
            <label for="note">Notiz</label>
            <textarea
              id="note"
              nbInput
              fullWidth
              rows="3"
              formControlName="note"
              placeholder="Grund der Änderung"
            ></textarea>
          </div>
        </div>

        <div class="warning" *ngIf="selectedRow && !selectedRow.enforce_token_quota">
          Token-Quota-Enforcement ist im aktiven Plan deaktiviert. Ein Limit wird gespeichert,
          aber vom Tutor nicht als harte Grenze erzwungen.
        </div>

        <div class="actions">
          <button nbButton type="button" status="basic" outline (click)="close()">
            Schließen
          </button>
          <button nbButton type="submit" status="primary" [nbSpinner]="saving" [disabled]="saving || !isChanged()">
            Speichern
          </button>
        </div>
      </form>

      <section class="audit">
        <h5>Änderungsverlauf</h5>
        <div class="audit-empty" *ngIf="quota.audit.length === 0">
          Noch keine manuellen Quota-Änderungen.
        </div>
        <div class="audit-list" *ngIf="quota.audit.length > 0">
          <div class="audit-row" *ngFor="let entry of quota.audit">
            <div>
              <strong>{{ formatNumber(entry.old_token_quota_limit) }}</strong>
              <span>→</span>
              <strong>{{ formatNumber(entry.new_token_quota_limit) }}</strong>
              <small>
                Remaining {{ formatNumber(entry.old_remaining_token_quota) }} →
                {{ formatNumber(entry.new_remaining_token_quota) }}
              </small>
            </div>
            <div>
              <span>{{ entry.changed_by_name || entry.changed_by_email || 'Unbekannt' }}</span>
              <small>{{ formatDate(entry.created_at) }}</small>
            </div>
            <p *ngIf="entry.note">{{ entry.note }}</p>
          </div>
        </div>
      </section>
    </ng-container>
    </details>
  </nb-card-body>
</nb-card>
